import dayjs from 'dayjs';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import {
  mapGetters,
  mapState,
  mapMutations,
  mapActions,
} from 'vuex';
import useVuelidate from '@vuelidate/core';

import { toSnakeCase, toCamelCase } from '@/utils/caseConvertor';
import {
  MemberSelector,
  OrderConfig,
  ProgressBar,
} from '@/components/Order';
import {
  CREATE_DRAFT,
  CREATE_ORDER,
  GET_DRAFT,
  SET_DEPARTMENT_LIST,
  SET_DRAFT_ORDER,
  UPDATE_DRAFT,
  UPDATE_DRAFT_ORDER,
} from '@/store/orders/constants';
import { GET_DEPARTMENTS } from '@/store/setting/constants';
import {
  ORDER_STATUS_WITH_TYPE_TEXT,
  ORDER_STEP_MAPPING,
  ORDER_STEP,
  SNACKBAR_TYPES,
} from '@/utils/constants';

export default {
  name: 'CreateOrder',
  components: {
    MemberSelector,
    OrderConfig,
    ProgressBar,
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      createdOrderId: null,
      currentStepIndex: 0,
      loading: false,
      stepMapping: ORDER_STEP_MAPPING,
    };
  },
  computed: {
    ...mapGetters('orders', [
      'currentDraftId',
      'draftBuyingMethod',
      'draftMenuId',
      'draftStoreId',
    ]),
    ...mapState(['isSidebarCollapsed']),
    ...mapState('orders', [
      'creatingOrder',
      'draftOrder',
      'isGettingDraft',
    ]),
    steps() {
      return [
        {
          description: '挑選出想參團一起訂的人員，增加後續訂購的準確性。',
          name: '誰要一起訂',
        },
        {
          description: '選取店家，設好時間一起訂。',
          name: '選擇訂哪家',
        },
        {
          description: '向店家下訂單，分發物品給夥伴。',
          name: '下訂與分發',
        },
      ];
    },
    currentStep: {
      get() {
        return ORDER_STEP[this.currentStepIndex];
      },
      set(step) {
        this.currentStepIndex = this.getStepIndex(step);
      },
    },
    inEndedOrOnGoing() {
      const methods = [
        ORDER_STATUS_WITH_TYPE_TEXT.ENDED,
        ORDER_STATUS_WITH_TYPE_TEXT.ONGOING,
      ];
      return methods.includes(this.draftBuyingMethod);
    },
    isStepMemberSelector() {
      return this.currentStep === ORDER_STEP_MAPPING.MEMBER_SELECTOR;
    },
    isStepOrderConfig() {
      return this.currentStep === ORDER_STEP_MAPPING.ORDER_CONFIG;
    },
    isLastStep() {
      return this.draftStoreId
        && this.draftMenuId
        && this.getStepIndex() === (ORDER_STEP.length - 2);
    },
    isLoading() {
      return this.isGettingDraft || this.loading || this.creatingOrder;
    },
    showActionBar() {
      if (this.currentStep === ORDER_STEP_MAPPING.ORDER_CONFIG) {
        return !!+this.draftMenuId;
      }
      return this.currentStep === ORDER_STEP_MAPPING.MEMBER_SELECTOR;
    },
    canSaveDraft() {
      return !this.v$.$invalid;
    },
    nextStepAvailability() {
      switch (this.currentStep) {
        case ORDER_STEP_MAPPING.MEMBER_SELECTOR:
          return !this.v$.$invalid;
        case ORDER_STEP_MAPPING.ORDER_CONFIG: {
          const isTimeBeforeNow = this.isTimeBeforeNow(this.draftOrder.endTime);
          if (isTimeBeforeNow && !this.inEndedOrOnGoing) {
            return false;
          }
          const invalid = this.v$.$invalid;
          return !invalid || this.creatingOrder;
        }
        default:
          return false;
      }
    },
    nextStepButtonText() {
      const mapping = {
        [ORDER_STEP_MAPPING.MEMBER_SELECTOR]: '確認並前往下一步',
        [ORDER_STEP_MAPPING.ORDER_CONFIG]: '發起訂購',
      };
      return mapping[this.currentStep] || '';
    },
    draftId: {
      get() {
        return this.currentDraftId;
      },
      set(draftId) {
        this.updateDraftOrder({ draftId });
      },
    },
  },
  async created() {
    try {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      this.loading = true;
      const { draftId } = this.$route.params;
      const departments = await this.getDepartments();
      this.setDepartmentList(departments);
      if (draftId) {
        await this.loadDraft(draftId);
      }
    } catch (error) {
      this.$snackbar({
        type: SNACKBAR_TYPES.ERROR,
        title: '載入資訊失敗，請重新整理網頁。',
        msg: error?.message || '發生未知錯誤，請再試一次',
      });
    } finally {
      this.loading = false;
    }
  },
  beforeDestroy() {
    this.draftId = null;
    this.setDraftOrder({});
  },
  methods: {
    ...mapActions('orders', {
      getDraft: GET_DRAFT,
      createOrder: CREATE_ORDER,
      createDraft: CREATE_DRAFT,
      updateDraft: UPDATE_DRAFT,
    }),
    ...mapActions('setting', {
      getDepartments: GET_DEPARTMENTS,
    }),
    ...mapMutations('orders', {
      setDepartmentList: SET_DEPARTMENT_LIST,
      setDraftOrder: SET_DRAFT_ORDER,
      updateDraftOrder: UPDATE_DRAFT_ORDER,
    }),
    doScheduleProcess(order) {
      if (!order) {
        return order;
      }
      const copy = { ...order };
      const { entreeLimits = [] } = copy || {};
      if (!Array.isArray(entreeLimits) && typeof entreeLimits === 'object' && entreeLimits !== null) {
        copy.entreeLimits = Object.keys(entreeLimits)
          .map((key) => ({ entree_index: key, ...entreeLimits[key] }));
      }
      if (!copy.enableSchedule) {
        delete copy.schedule;
      }
      if (copy.enableSchedule && isEmpty(copy.schedule)) {
        copy.enableSchedule = false;
        delete copy.schedule;
      }
      if (isEmpty(copy.schedule)) {
        delete copy.schedule;
      }

      return copy;
    },
    getStepIndex(step = this.currentStep) {
      return ORDER_STEP.indexOf(step);
    },
    goBack() {
      const { query: { tab } = {} } = this.$route || {};
      const isValidTab = Object.values(ORDER_STATUS_WITH_TYPE_TEXT).includes(tab);
      const query = isValidTab ? { tab } : {};
      this.$router.push({
        path: '/order/list',
        query,
      });
    },
    isTimeBeforeNow(time) {
      const now = dayjs();
      return dayjs(time).isBefore(now);
    },
    async onSubmit() {
      if (this.getStepIndex() < (ORDER_STEP.length - 2)) {
        await this.saveDraft();
        this.nextStep();
        return;
      }
      try {
        await this.saveDraft({ silent: true });

        const { id: draftId, data } = this.getDraftWithDraftId();
        const payload = this.doScheduleProcess(data);
        const param = toCamelCase(payload);
        const order = toSnakeCase({
          ...param,
          draftId,
        });

        const { id: orderId } = await this.createOrder(order);

        this.createdOrderId = orderId;
        this.$router.push(`/order/${orderId}?created=true`);
      } catch (error) {
        this.$snackbar({
          type: SNACKBAR_TYPES.ERROR,
          title: '訂單建立失敗，請稍後再試',
          msg: error?.message || '發生未知錯誤，請再試一次',
        });
      }
    },
    getDraftWithDraftId(id = this.draftId, payload = {}) {
      const data = this.getDraftOrderData(payload);
      return id ? { id, data } : data;
    },
    getDraftOrderData(payload = {}) {
      const order = cloneDeep(this.draftOrder);

      return {
        ...order,
        step: this.currentStep,
        ...payload,
      };
    },
    setStep(step = ORDER_STEP_MAPPING.STORE_SELECTOR) {
      this.currentStep = step;
    },
    nextStep() {
      const currentStepIndex = ORDER_STEP.findIndex((step) => step === this.currentStep);
      this.setStep(ORDER_STEP[currentStepIndex + 1]);
    },
    async saveDraft({ silent = false } = {}) {
      this.loading = true;
      const draft = this.getDraftWithDraftId();
      const order = toSnakeCase({
        ...draft,
        data: this.doScheduleProcess(draft.data),
      });

      if (this.draftId) {
        try {
          await this.updateDraft(order);
          if (!silent) {
            this.$snackbar({
              type: SNACKBAR_TYPES.SUCCESS,
              title: '草稿已更新',
            });
          }
        } catch (error) {
          if (silent) {
            throw error;
          }
          this.$snackbar({
            type: SNACKBAR_TYPES.ERROR,
            title: '目前無法更新草稿',
            msg: error?.message || '發生未知錯誤，請再試一次',
          });
        } finally {
          this.loading = false;
        }
      } else {
        try {
          const { id } = await this.createDraft(order);
          this.draftId = id;

          if (!silent) {
            this.$snackbar({
              type: SNACKBAR_TYPES.SUCCESS,
              title: '已儲存草稿',
            });
          }
        } catch (error) {
          if (silent) {
            throw error;
          }
          this.$snackbar({
            type: SNACKBAR_TYPES.ERROR,
            title: '目前無法更新草稿',
            msg: error?.message || '發生未知錯誤，請再試一次',
          });
        } finally {
          this.loading = false;
        }
      }
    },
    async loadDraft(id) {
      const { step } = await this.getDraft({ id });
      this.draftId = id;
      this.currentStepIndex = this.getStepIndex(step);
    },
    async updateNewDraft() {
      const { id, data } = this.getDraftWithDraftId();
      const payload = this.doScheduleProcess(data);
      await this.updateDraft(toSnakeCase({ id, data: payload }));
    },
  },
};
